import dot from 'dot-prop-immutable';
import { path } from 'ramda';
import { REHYDRATE } from 'redux-persist';

import { PREFERRED_UNIT_SYSTEMS } from '@frontend/api';
import { DATE_FORMATS } from '@frontend/utils';

import { createRehydrateReducer } from 'modules/common/redux';

import { getEntity, getEntities, denormalize } from 'store/entities/selectors';
import * as schema from 'store/quotes/schema';

import { createNumericComparator } from 'utils/helpers';

import { types } from './actions';

const initialState = {
  quote: {},
  contactId: null,
  displaySettings: {},
  expandedId: null,
  quoteId: null,
  quoteNumberWithRevision: null,
  showCart: false,
  showQuoteInstructions: true,
  isThankYouModalOpen: false,
  isShareQuoteModalOpen: false,
  quoteLinkForShare: null,
  selectedOptions: {},
  confirmedSelectedOptions: {},
  hasSubmitted: false,
  preferredUnitSystem: PREFERRED_UNIT_SYSTEMS.IMPERIAL,
  preferredDateFormat: DATE_FORMATS.MM_DD_YYYY,
};

const getScope = (state) => state.modules.digiQuote.digiQuotePage;

const rehydrateReducer = createRehydrateReducer(getScope, ['selectedOptions']);

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case REHYDRATE: {
      const nextState = rehydrateReducer(state, action);
      return {
        ...nextState,
        confirmedSelectedOptions: nextState.confirmedSelectedOptions,
      };
    }
    case types.INIT_SELECTED_OPTIONS:
      return {
        ...state,
        selectedOptions: action.payload.selectedOptions,
      };
    case types.INIT_DIGIQUOTE:
      return {
        ...state,
        quote: action.payload.quote,
        contactId: action.payload.contactId,
        showPaperlessBranding: action.payload.showPaperlessBranding,
        displaySettings: action.payload.displaySettings,
        expandedId: null,
        hasSubmitted: false,
        quoteId: action.payload.quoteId,
        quoteNumberWithRevision: action.payload.quoteNumberWithRevision,
        showQuoteInstructions: !Object.keys(state.selectedOptions).length,
        preferredUnitSystem: action.payload.preferredUnitSystem,
        preferredDateFormat: action.payload.preferredDateFormat,
      };
    case types.OPEN_CART:
      return {
        ...state,
        showCart: true,
      };
    case types.CLOSE_CART:
      return {
        ...state,
        showCart: false,
      };
    case types.CONFIRM_QUOTE_ITEM_CONFIGURATION:
      return {
        ...state,
        confirmedSelectedOptions: {
          ...state.selectedOptions,
        },
      };
    case types.SET_HAS_SUBMITTED:
      return {
        ...state,
        hasSubmitted: true,
      };
    case types.UPDATE_QUOTE_ITEM_CONFIGRATION: {
      let nextState = state;
      const path = `selectedOptions.${action.payload.quoteItemId}`;
      if (action.payload.quantityId === null) {
        nextState = dot.delete(nextState, path);
      } else {
        nextState = dot.set(nextState, path, {
          quantityId: action.payload.quantityId,
          expediteId: action.payload.expediteId,
          addOnCellIds: action.payload.addOnCellIds,
        });
      }
      nextState = dot.set(nextState, 'showQuoteInstructions', false);
      return nextState;
    }
    case types.OPEN_THANK_YOU_MODAL:
      return {
        ...state,
        isThankYouModalOpen: true,
      };
    case types.CLOSE_THANK_YOU_MODAL:
      return {
        ...state,
        isThankYouModalOpen: false,
      };
    case types.SHOW_QUOTE_INSTRUCTIONS:
      return {
        ...state,
        showQuoteInstructions: true,
      };
    case types.HIDE_QUOTE_INSTRUCTIONS:
      return {
        ...state,
        showQuoteInstructions: false,
      };
    case types.OPEN_SHARE_QUOTE_MODAL:
      return {
        ...state,
        isShareQuoteModalOpen: true,
        quoteLinkForShare: action.payload.digitalQuoteUrl,
      };
    case types.CLOSE_SHARE_QUOTE_MODAL:
    case types.COPY_QUOTE_LINK_SUCCESS:
    case types.COPY_QUOTE_LINK_ERROR:
      return {
        ...state,
        isShareQuoteModalOpen: false,
      };
    default:
      return state;
  }
}

export const getQuoteId = (state) => getScope(state).quoteId;

export const getQuote = (state) => getScope(state).quote;

export const getAllQuoteItemIds = (state) =>
  Object.keys(getEntities(state, 'quoteItems'));

export const getQuoteItemById = (state, quoteItemId) =>
  getEntity(state, 'quoteItems', quoteItemId);

export const getQuoteItemByIdDenormalized = (state, quoteItemId) =>
  denormalize(state, quoteItemId, schema.quoteItem);

export const getToken = (state) => (getQuote(state) || {}).token;

export const getParentQuote = (state) => (getQuote(state) || {}).parentQuote;

export const getParentQuoteToken = (state) =>
  (getParentQuote(state) || {}).digiQuoteKey;

export const getQuoteSupplierLocator = (state) =>
  path(['locator'], getQuote(state));

export const getQuoteItems = (state) => {
  return denormalize(state, getAllQuoteItemIds(state), schema.quoteItems)
    .filter(Boolean)
    .filter((qi) => qi.quoteId === getQuoteId(state))
    .slice()
    .sort(createNumericComparator('position'));
};

export const getAccount = (state) => getQuote(state)?.account;

export const getContact = (state) => getQuote(state)?.contact;

export const getQuoteNotes = (state) => getQuote(state)?.quoteNotes;

export const getTermsAndConditions = (state) => getQuote(state)?.terms;

export const getExpandedId = (state) => getScope(state).expandedId;

export const getQuoteItemConfiguration = (state) =>
  getScope(state).selectedOptions || {};

export const getSelectedOptions = (state) =>
  getScope(state).selectedOptions || {};

export const getSelectedOptionsForQuoteItems = (state, quoteItems) => {
  const selectedOptions = getSelectedOptions(state);
  return quoteItems
    .map((quoteItem) => selectedOptions[quoteItem.id])
    .filter(Boolean);
};

export const getSelectedQuoteItems = (state, quoteItems) => {
  const selectedOptions = getSelectedOptions(state);
  return quoteItems.filter((quoteItem) => selectedOptions[quoteItem.id]);
};

export const getQuoteLinkForShare = (state) =>
  getScope(state).quoteLinkForShare;

export const getIsPreview = (state) => (getQuote(state) || {}).isPreview;

export const getShowQuoteInstructions = (state) =>
  (getScope(state) || {}).showQuoteInstructions;

export const getShowCart = (state) => (getScope(state) || {}).showCart;

export const getDigiQuoteSubTotalPrice = (state) => {
  const quoteItems = getQuoteItems(state);
  const configuration = getQuoteItemConfiguration(state);
  const selectedOptions = getSelectedOptions(state);
  return quoteItems.reduce((sum, quoteItem) => {
    const quantities = quoteItem.rootComponent.quantities || [];
    const { quantityId, expediteId } = configuration[quoteItem.id] || {};
    const selectedQuantity =
      quantities.find((qty) => quantityId == qty.id) || {};
    const selectedExpedite =
      (selectedQuantity.expedites || []).find(
        (expedite) => expedite.id == expediteId,
      ) || null;
    const additionalPrice = selectedExpedite
      ? selectedExpedite.totalPrice
      : selectedQuantity.totalPrice
      ? selectedQuantity.totalPrice
      : 0;
    let addOnsPrice = 0;
    const options = selectedOptions[quoteItem.id];
    if (options && options.addOnCellIds) {
      options.addOnCellIds.forEach((cellId) => {
        const cell = quoteItem.rootComponent.addOnCells.find(
          (c) => c.id == cellId,
        );
        cell &&
          (addOnsPrice += parseFloat(
            cell.manualPrice ? cell.manualPrice : cell.price,
          ));
      });
    }
    return sum + parseFloat(additionalPrice) + addOnsPrice;
  }, 0);
};

export const getIsShareQuoteModalOpen = (state) =>
  (getScope(state) || {}).isShareQuoteModalOpen;

export const getIsThankYouModalOpen = (state) =>
  getScope(state).isThankYouModalOpen;

export const getHasSubmitted = (state) => getScope(state).hasSubmitted;

export const getDisplaySettings = (state) => getScope(state).displaySettings;

export const getShouldShowPaperlessBranding = (state) =>
  getScope(state).showPaperlessBranding;

export const getShouldShowPartDetails = (state) =>
  Object.values(getDisplaySettings(state)).includes(true);

export const getPreferredUnitSystem = (state) =>
  getScope(state).preferredUnitSystem;

export const getPreferredDateFormat = (state) =>
  getScope(state).preferredDateFormat;
